import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { ElementConfig } from '../ui-element.type';
import { UIOption } from './ui-option';
import { UIIconGlyphRedesign, UIIconColor } from '../icon/ui-icon.types';
import styles from './ui-option-icon.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIOption}
 * @alias UIOptionIcon
 * @element ui-option-icon
 * @classdesc Represents a class for <code>ui-option-icon</code> element.
 * Similar to ui-option, but holds icon. Used with
 * <code>ui-dropdown layout="icon"</code> and <code>ui-option-list</code>.
 * @property {boolean} optgroup {@attr optgroup} Marks this option as a group header.
 * @property {boolean} control Flag to distinguish the location where option will be shown.
 * @property {UIIconGlyphRedesign} [icon] {@attr icon} Icon glyph.
 * @property {UIIconColor} [color] {@attr color} Icon color.
 * @slot
 * @example
 * <ui-dropdown layout="icon">
 *   <select>
 *     <option data-color="pineapple" data-icon="star-full">Superstar</option>
 *     <option data-color="red" data-icon="heart" value="health">Health</option>
 *   </select>
 * </ui-dropdown>
 * @example
 * <ui-option-list label="Select country">
 *   <ui-option-icon icon="redesign-flag-ee" text="Estonia" value="ee"></ui-option-icon>
 *   <ui-option-icon icon="redesign-flag-lv" text="Latvia" value="lv" selected></ui-option-icon>
 *   <ui-option-icon icon="redesign-flag-lt" text="Lithuania" value="lt"></ui-option-icon>
 * </ui-option-list>
 */

@Component({
    tag: 'ui-option-icon',
    styles,
})
export class UIOptionIcon extends UIOption {
    @Attribute(String) icon?: UIIconGlyphRedesign;
    @Attribute(String) color?: UIIconColor;

    static get observedAttributes(): Array<string> {
        return ['selected', 'selectable', 'text', 'icon'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        if (name === 'icon') {
            if (this.hydrated) {
                this.updateIcon(newValue);
            }
            return;
        }
        super.observeAttributes(name, oldValue, newValue);
    }

    /**
     * Updates the painted icon when the host icon attribute changes.
     * @param {string | null} newValue
     * @private
     */
    private updateIcon(newValue: string | null): void {
        const iconEl = this.querySelector('.ui-option-icon__icon');
        if (iconEl) {
            if (newValue) {
                iconEl.setAttribute('glyph', newValue);
            } else {
                iconEl.remove();
            }
            return;
        }
        if (!newValue) {
            return;
        }
        const icon = this.createElement({
            tagName: 'ui-icon',
            attributes: {
                class: 'ui-option-icon__icon',
                glyph: newValue,
                color: this.color,
            },
        });
        const label = this.querySelector('.ui-option-icon__label');
        this.insertBefore(icon, label);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.updateClassList({
            'ui-option-icon': true,
        });

        this.insertElements(
            (this.optgroup
                ? [this.buildOptGroupLabel()]
                : [
                      this.icon
                          ? {
                                tagName: 'ui-icon',
                                attributes: {
                                    class: 'ui-option-icon__icon',
                                    glyph: this.icon,
                                    color: this.color,
                                },
                            }
                          : undefined,
                      {
                          tagName: 'span',
                          attributes: {
                              class: 'ui-option-icon__label ui-option__label',
                          },
                          children: [
                              this.getAttribute('text') ||
                                  this.getAttribute('value') ||
                                  '',
                          ],
                      },
                  ].filter(Boolean)) as Array<
                HTMLElement | DocumentFragment | ElementConfig
            >
        );

        super.render();
    }
}
